/**
 * MissionNudge — floating contextual banner that reminds guests they
 * have an active Mega Mission relevant to the page they're on.
 *
 * Usage:
 *   <MissionNudge event={event} actions={["pocketbooth"]} />
 *
 * How it works:
 *   1. Reads the guest's active missions from localStorage
 *      (`eventdrop_missions_{eventId}`) — set by MegaMissions on GuestHome.
 *   2. Fetches the public mission catalog to find any mission whose
 *      `action` matches one in the `actions` prop AND is currently in
 *      `active` status with a live deadline.
 *   3. Renders a sticky pill on top of the page with countdown + title.
 *      Auto-hides when no matching mission is live.
 *
 * This is what turns "Mega Missions exists somewhere" into "oh snap,
 * I'm literally 2 minutes away from a Bronze right now."
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Target, Clock } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (secs) => {
  if (secs <= 0) return '0:00';
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const BADGE_COLOR = { bronze: '#CD7F32', silver: '#C0C0C0', gold: '#FFD700' };

const MissionNudge = ({ event, actions = [] }) => {
  const [missions, setMissions] = useState([]);
  const [state, setState] = useState({});
  const [now, setNow] = useState(Date.now());

  // Watch localStorage for cross-tab updates + refresh when active changes
  useEffect(() => {
    if (!event?.event_id) return;
    const load = () => {
      try {
        const raw = localStorage.getItem(`eventdrop_missions_${event.event_id}`);
        setState(raw ? JSON.parse(raw) : {});
      } catch {}
    };
    load();
    const iv = setInterval(load, 2000);
    return () => clearInterval(iv);
  }, [event?.event_id]);

  useEffect(() => {
    if (!event?.event_id) return;
    axios.get(`${API}/events/${event.event_id}/missions`)
      .then(r => setMissions(r.data.missions || []))
      .catch(() => {});
  }, [event?.event_id]);

  // Live 1s tick when there's anything to render
  const active = useMemo(() => {
    for (const m of missions) {
      if (!actions.includes(m.action)) continue;
      const s = state[m.id];
      if (!s || s.status !== 'active') continue;
      if (Date.now() > (s.deadline || 0)) continue;
      return { mission: m, state: s };
    }
    return null;
  }, [missions, state, actions, now]);

  useEffect(() => {
    if (!active) return;
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [active]);

  if (!active) return null;
  const { mission, state: s } = active;
  const remaining = Math.max(0, Math.floor((s.deadline - Date.now()) / 1000));
  const target = mission.target || 1;
  const progress = s.progress || 0;
  const color = BADGE_COLOR[mission.badge] || '#B89762';

  return (
    <div
      className="fixed top-3 left-1/2 -translate-x-1/2 z-[55] max-w-[92%]"
      data-testid="mission-nudge"
    >
      <div
        className="flex items-center gap-2.5 pl-3 pr-4 py-2 rounded-full shadow-2xl"
        style={{
          background: 'linear-gradient(135deg, rgba(15,18,34,0.96), rgba(26,31,58,0.96))',
          border: `1px solid ${color}`,
          boxShadow: `0 8px 32px ${color}55, 0 0 0 1px rgba(255,255,255,0.05) inset`,
          backdropFilter: 'blur(10px)',
        }}
      >
        <span
          className="text-xl leading-none flex-shrink-0"
          style={{ filter: `drop-shadow(0 0 6px ${color}aa)` }}
          aria-hidden="true"
        >
          {mission.emoji}
        </span>
        <div className="min-w-0">
          <p className="text-[10px] font-black uppercase tracking-widest leading-none mb-0.5" style={{ color }}>
            <Target size={10} weight="fill" className="inline mr-1" />
            Mission active
          </p>
          <p className="text-white text-xs font-bold truncate max-w-[60vw]">
            {mission.title}
          </p>
        </div>
        <div className="flex items-center gap-1 text-white font-mono font-bold text-xs tabular-nums pl-2 border-l border-white/15" style={{ color }}>
          <Clock size={11} weight="bold" /> {fmt(remaining)}
        </div>
        {target > 1 && (
          <div className="text-[10px] font-bold text-white/60">
            {progress}/{target}
          </div>
        )}
      </div>
    </div>
  );
};

export default MissionNudge;
